import _find from 'lodash/find';
import * as api from '../../api';
import {
  fetchActiveUser,
  inviteMultipleTeachers,
  inviteTeacher,
} from '../users/actions';
import { initializeFields } from '../fields/actions';
import { sendAnalyticsEventById } from '../stats/actions';
import { getNormalizedErrorMessage } from '../../errors';
import { setTokenCookie, updateSessionStorageJSON } from '../../utilities';
import { handleError, receiveUsers } from '../actionHelpers';
import { retryOperation } from './helpers';
import { getForm, getFormFields, isSensitiveTokenExpired } from '../reducers';
import { setSentryUser } from 'Utils/errorTracking';

export const initializeForm = (
  formName,
  formId,
  initialFormValues = {},
  initialFieldValues = {}
) => (dispatch) => {
  dispatch({
    type: 'INITIALIZE_FORM',
    formName,
    formId,
    initialFormValues,
  });
  dispatch(initializeFields(formName, formId, initialFieldValues));
};
export const resetForm = (formName, formId) => (dispatch) => {
  dispatch({
    type: 'RESET_FORM',
    formName,
    formId,
  });
};

export const clearFormMessage = (formName, formId) => (dispatch) => {
  dispatch({
    type: 'CLEAR_FORM_MESSAGE',
    formName,
    formId,
  });
};

export const setFormMessage = (formName, formId, message) => (dispatch) => {
  dispatch({
    type: 'SET_FORM_MESSAGE',
    formName,
    formId,
    message,
  });
};

export const setFormMessageById = (formName, formId, id) => (dispatch) => {
  const message = getNormalizedErrorMessage(id, null);
  dispatch(setFormMessage(formName, formId, message));
};

export const enableFormReconciliationMode = (formName, formId) => (
  dispatch
) => {
  dispatch({
    type: 'ENABLE_FORM_RECONCILIATION_MODE',
    formName,
    formId,
  });
};

export const requireTokenUpdate = (formName, formId) => (dispatch) => {
  dispatch({
    type: 'FORM_REQUIRES_UPDATED_TOKEN',
    formName,
    formId,
  });
};

export const resolveFormTokenUpdate = (formName, formId) => (dispatch) => {
  dispatch({
    type: 'FORM_RESOLVE_UPDATED_TOKEN',
    formName,
    formId,
  });
};

export const toggleReadOnly = (formName, formId, readOnly) => (dispatch) => {
  dispatch({
    type: 'SET_FORM_READONLY_VALUE',
    formName,
    formId,
    readOnly,
  });
};

export const setFormAsSubmitted = (formName, formId) => (dispatch) => {
  dispatch({
    type: 'SUBMIT_FORM_SUCCESS',
    formName,
    formId,
  });
};

export const removeSingleTeacherForm = (originalEmail, formId) => (
  dispatch
) => {
  dispatch({
    type: 'CLEAR_AN_INVITED_TEACHER',
    originalEmail,
    formId,
  });
};

const formNameToApiMethod = {
  addStudentToClass: api.addNewStudentToClass,
  addStudentToSchool: api.addNewStudentToSchool,
  teacherLogin: api.postLogin,
  studentLogin: api.postStudentLogin,
  loginAgain: api.postLogin,
  changePassword: api.postLoginBeforePasswordChange,
  createClass: api.createClass,
  acceptTeacherInvitation: api.patchInvitedUser,
  freeTrial: api.postFreeTrial,
  forgotPassword: api.postForgotPassword,
  resetPassword: api.resetPassword,
  accountBasics: api.patchUser,
  editTeacher: api.patchUser,
  editStudent: api.patchUser,
  studentSchoolCode: api.validateSchoolCode,
  paymentUsername: api.checkUsernameValidity,
};

const successCallback = (
  formName,
  formId,
  dispatch,
  response,
  requestArgs,
  fields
) => {
  switch (formName) {
    case 'addStudentToClass':
      dispatch(initializeFields(formName, formId));
      dispatch(sendAnalyticsEventById('added_a_student'));
      dispatch(
        receiveUsers(response, 'ADD_STUDENT_TO_CLASS', { ...requestArgs })
      );
      break;
    case 'addStudentToSchool':
      dispatch(initializeFields(formName, formId));
      dispatch(sendAnalyticsEventById('added_a_student'));
      dispatch(
        receiveUsers(response, 'ADD_USER_TO_USER_GROUP', {
          ...requestArgs,
          name: 'students_in_school',
        })
      );
      break;
    case 'forgotPassword':
      dispatch(initializeFields(formName, formId));
      break;
    case 'teacherLogin':
    case 'studentLogin':
      setTokenCookie(response.results.token);
      dispatch({
        type: 'POST_LOGIN_SUCCESS',
        token: response.results.token,
      });
      dispatch(fetchActiveUser());
      break;
    case 'loginAgain': {
      const { referringFormId, referringFormName } = requestArgs;
      dispatch({
        type: 'UPDATE_AUTH_TOKEN',
        token: response.results.token,
      });
      if (referringFormId && referringFormName)
        dispatch(resolveFormTokenUpdate(referringFormName, referringFormId));
      break;
    }
    case 'createClass':
      dispatch(sendAnalyticsEventById('created_a_class'));
      dispatch({
        type: 'POST_CLASS_SUCCESS',
        id: response.results[0],
      });
      break;
    case 'accountBasics':
    case 'editTeacher':
    case 'editStudent':
      dispatch(sendAnalyticsEventById('edited_a_user'));
      dispatch({
        type: 'USER_ACCOUNT_SETTINGS_UPDATED',
        id: requestArgs.id,
        fields,
      });
      Object.keys(fields).map((key) => {
        return dispatch({
          type: 'UPDATE_FIELD_ORIGINAL_VALUE',
          fieldName: `${formId}__${key}`,
          originalValue: fields[key].value,
        });
      });
      break;
    case 'changePassword':
      api.changePassword(fields, requestArgs, response.results.token).then(
        (changeResponse) => {
          if (changeResponse.errorMsg) {
            handleError(dispatch, changeResponse, 'SUBMIT_FORM_FAILURE', {
              formName,
              formId,
            });
          } else {
            dispatch({
              type: 'USER_ACCOUNT_SETTINGS_UPDATED',
              id: requestArgs.id,
              fields,
            });
            dispatch({
              type: 'SUBMIT_FORM_SUCCESS',
              formName,
              formId,
            });
            dispatch(
              initializeFields(formName, formId, {
                username: {
                  value: fields.username.value,
                },
              })
            );
          }
        },
        (error) =>
          handleError(dispatch, error, 'SUBMIT_FORM_FAILURE', {
            formName,
            formId,
          })
      );
      break;
    case 'studentSchoolCode':
      dispatch({
        type: 'SCHOOL_CODE_IS_VALID',
        response,
        schoolCode: fields.accessCode.value,
      });
      break;
    case 'freeTrial':
      setTokenCookie(response.results.token);
      dispatch({
        type: 'POST_FREE_TRIAL_SIGNUP_SUCCESS',
        token: response.results.token,
      });
      dispatch(fetchActiveUser());
      break;
    case 'acceptTeacherInvitation':
    case 'resetPassword': {
      // The API response doesn’t have a list of users
      // So we’ll make one.
      const responseWithResultsList = {
        ...response,
        results: [response.results],
      };
      const tokenActionName =
        formName === 'acceptTeacherInvitation'
          ? 'INVITED_TEACHER_SIGN_UP_SUCCESS'
          : 'RESET_PASSWORD_SUCCESS';
      dispatch(
        receiveUsers(responseWithResultsList, tokenActionName, {
          ...requestArgs,
        })
      );
      dispatch(fetchActiveUser());
      break;
    }
    default:
      break;
  }
};

export const submitForm = (formName, formId, requestArgs = {}) => (
  dispatch,
  getState
) => {
  const state = getState();
  const { token } = state.users.auth;
  const fields = getFormFields(state, formId);
  const form = getForm(state, formId);
  const fieldsByName = {};
  Object.keys(fields).forEach((key) => {
    const newKey = key.replace(`${formId}__`, '');
    fieldsByName[newKey] = fields[key];
  });
  if (form.onlySubmitChanges) {
    // Only include fields with updated values
    Object.keys(fieldsByName).forEach((key) => {
      const field = fieldsByName[key];
      if (field.value === field.originalValue) delete fieldsByName[key];
    });

    // Require the user log in again if their sensitive token expired
    const hasSensitiveFields = _find(fieldsByName, {
      requiresSensitiveToken: true,
    });
    if (isSensitiveTokenExpired(state) && hasSensitiveFields) {
      return dispatch(requireTokenUpdate(formName, formId));
    }
    // Don’t bother submitting if nothing has changed
    if (Object.keys(fieldsByName).length === 0) {
      const message = {
        type: 'info',
        msgId: 'form.no_changes_message',
      };
      return dispatch(setFormMessage(formName, formId, message));
    }
  }

  dispatch({
    type: 'SUBMIT_FORM_REQUEST',
    formName,
    formId,
    ...requestArgs,
  });

  // This feels inelegant. Maybe we need a similar group of forms which
  // require a loop of API requests. Something to ponder for later.
  if (formName === 'inviteMultipleTeachers') {
    dispatch(sendAnalyticsEventById('invited_teachers'));
    const { schoolId } = state.users.auth;
    return inviteMultipleTeachers(
      requestArgs.emailAddresses,
      schoolId,
      token,
      dispatch,
      state
    );
  }
  if (formName === 'inviteSingleTeacher') {
    const { schoolId } = state.users.auth;
    const { value, originalValue } = fieldsByName.username;
    return inviteTeacher(
      value,
      originalValue,
      schoolId,
      token,
      dispatch,
      state
    );
  }

  const apiMethod = formNameToApiMethod[formName];
  return apiMethod(fieldsByName, requestArgs, token).then(
    (response) => {
      if (response.errorMsg) {
        handleError(dispatch, response, 'SUBMIT_FORM_FAILURE', {
          formName,
          formId,
          fieldsByName,
        });
      } else {
        dispatch({
          type: 'SUBMIT_FORM_SUCCESS',
          formName,
          formId,
        });
        successCallback(
          formName,
          formId,
          dispatch,
          response,
          requestArgs,
          fieldsByName
        );
      }
    },
    (error) =>
      handleError(dispatch, error, 'SUBMIT_FORM_FAILURE', {
        formName,
        formId,
        fieldsByName,
      })
  );
};

export const submitPaymentForm = (formName, formId, requestArgs = {}) => (
  dispatch,
  getState
) => {
  const state = getState();
  const { token } = state.users.auth;
  const fields = getFormFields(state, formId);
  const fieldsByName = {};
  Object.keys(fields).forEach((key) => {
    const newKey = key.replace(`${formId}__`, '');
    fieldsByName[newKey] = fields[key];
  });

  dispatch({
    type: 'SUBMIT_FORM_REQUEST',
    formName,
    formId,
    ...requestArgs,
  });

  return api
    .createSchoolSubscription(fieldsByName, requestArgs, token)
    .then((response) => {
      const { errorCode, status, meta } = response;
      if (errorCode || (status && status >= 500) || meta.status >= 400) {
        handleError(
          dispatch,
          {
            ...response,
            errorMsg: response.errorMsg || response.message, // handles stripe errors
          },
          'SUBMIT_FORM_FAILURE',
          {
            formName,
            formId,
            fieldsByName,
          }
        );
        return;
      }

      dispatch({
        type: 'SUBMIT_FORM_SUCCESS',
        formName,
        formId,
      });
      successCallback(
        formName,
        formId,
        dispatch,
        response,
        requestArgs,
        fieldsByName
      );
    });
};

export const submitAddPaymentMethodForm = (
  formName,
  formId,
  requestArgs = {}
) => (dispatch, getState) => {
  const state = getState();
  const { token } = state.users.auth;
  const fields = getFormFields(state, formId);
  const fieldsByName = {};
  Object.keys(fields).forEach((key) => {
    const newKey = key.replace(`${formId}__`, '');
    fieldsByName[newKey] = fields[key];
  });

  dispatch({
    type: 'SUBMIT_FORM_REQUEST',
    formName,
    formId,
    ...requestArgs,
  });

  return api
    .addPaymentMethod(fieldsByName, requestArgs, token)
    .then((response) => {
      const { errorCode, status, meta } = response;
      if (errorCode || (status && status >= 500) || meta.status >= 400) {
        handleError(
          dispatch,
          {
            ...response,
            errorMsg: response.errorMsg || response.message, // handles stripe errors
          },
          'SUBMIT_FORM_FAILURE',
          {
            formName,
            formId,
            fieldsByName,
          }
        );
        return;
      }

      dispatch({
        type: 'SUBMIT_FORM_SUCCESS',
        formName,
        formId,
      });
      successCallback(
        formName,
        formId,
        dispatch,
        response,
        requestArgs,
        fieldsByName
      );
    });
};

export const initializeMultipageForm = (id, initialValues) => (
  dispatch,
  getState
) => {
  const { multipageForms } = getState().forms;
  if (multipageForms[id] && Object.keys(multipageForms[id]).length > 0)
    return null;
  if (initialValues) updateSessionStorageJSON(id, initialValues);
  return dispatch({
    type: 'INITIALIZE_MULTIPAGE_FORM',
    id,
    initialValues,
  });
};

export const clearMultipageForm = (id) => (dispatch) => {
  return dispatch({
    type: 'CLEAR_MULTIPAGE_FORM',
    id,
  });
};

export const updateMultipageFormValues = (id, values) => (dispatch) => {
  updateSessionStorageJSON(id, values);
  return dispatch({
    type: 'UPDATE_MULTIPAGE_FORM',
    id,
    values,
  });
};
